<template>
    <el-dialog :title="(old.id ? 'Update ': 'Add a new ')+'Risk Area'"
               :visible.sync="visible"
               width="60%"
               :close-on-click-modal="false">
        <div class="dialog-content">
            <el-form :rules="rules" ref="riskAreaForm" :model="form" @submit="submitForm" label-position="left"
                     size="small" label-width="100px">
                <el-form-item label="Name" prop="name">
                    <el-input v-model="form.name" placeholder="Enter Risk Area Name"></el-input>
                </el-form-item>
                <el-form-item label="Abbreviation" prop="abbreviation">
                    <el-input v-model="form.abbreviation"
                              placeholder="Enter Risk Area Abbreviation"></el-input>
                </el-form-item>
                <el-form-item label="Status" prop="active">
                    <el-select v-model="form.active" placeholder="Select Status">
                        <el-option
                                v-for="(name, index) in activeMapping"
                                :key="name"
                                :label="name"
                                :value="index">
                        </el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="Order" prop="order">
                    <el-input-number v-model.number="form.order" :min="1" controls-position="right"></el-input-number>
                </el-form-item>
                <el-form-item label="Type" prop="type">
                    <el-select :disabled="Boolean(old.id)" size="medium" v-model="form.type">
                        <el-option v-for="obj in riskAreaType" :key="obj.value" :label="obj.label"
                                   :value="obj.value"></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="Description" prop="description">
                    <el-input type="textarea" v-model="form.description"
                              rows="6"
                              placeholder="Add Risk Area Description"></el-input>
                </el-form-item>
            </el-form>
            <div slot="footer" class="dialog-footer">
                <el-button @click="visible = false">Cancel</el-button>
                <el-button type="primary" @click="submitForm">Confirm</el-button>
            </div>
        </div>
    </el-dialog>
</template>

<script>
    import {API} from "@/api/index";
    import {createNamespacedHelpers} from 'vuex';

    const {mapState, mapActions, mapMutations} = createNamespacedHelpers('riskIndex');

    export default {
        name:     "AddEditRiskAreaDialog",
        data() {
            let checkDuplicateAbbreviation = (rule, value, callback) => {
                if (rule.ref.old.abbreviation !== value && rule.ref.riskAreaData[value]) {
                    return callback(new Error('Please enter unique Risk Area Abbreviation'));
                } else if (/[^A-Z0-9-]/g.test(value)) {
                    return callback(new Error('Only uppercase, numbers(0-9) and dash(-) are allowed'));
                }
                callback();
            };
            return {
                visible:       false,
                form:          {
                    description:  '',
                    active:       0,
                    name:         '',
                    abbreviation: '',
                    order:        1,
                    type:         'quant'
                },
                old:           {},
                activeMapping: [
                    'Inactive',
                    'Active',
                    'Under Analysis'
                ],
                rules:         {
                    'description':  [
                        {required: true, message: 'Please enter Risk Area Description', trigger: 'blur'},
                        {min: 2, message: 'Length should be more than 3 characters.', trigger: 'blur'}
                    ],
                    'active':       [
                        {required: true, message: 'Please select Risk Area Status', trigger: 'blur'}
                    ],
                    'name':         [
                        {required: true, message: 'Please enter a Risk Area Name', trigger: 'blur'},
                        {min: 5, message: 'Length should be more than 5 characters', trigger: 'blur'}
                    ],
                    'abbreviation': [
                        {required: true, message: 'Please enter Risk Area Abbreviation', trigger: 'blur'},
                        {min: 2, message: 'Length should be more than 3 characters.', trigger: 'blur'},
                        {validator: checkDuplicateAbbreviation, trigger: 'blur', ref: this}
                    ],
                    'order':        [
                        {required: true, message: 'Please set Risk Area Order', trigger: 'blur'},
                        {type: 'number', min: 1, message: 'Order cannot be less than 1', trigger: 'blur'}
                    ]
                },
                riskAreaType:  [
                    {
                        value: 'quant',
                        label: 'Quantitative'
                    }, {
                        value: 'qual',
                        label: 'Qualitative'
                    }
                ]
            };
        },
        methods:  {
            ...mapActions([
                'loadRiskAreaData'
            ]),
            ...mapMutations({
                addState:    'ADD_RISK_AREA_DATA',
                updateState: 'UPDATE_RISK_AREA_DATA',
                deleteState: 'DELETE_RISK_AREA_DATA'
            }),
            open:       function (abbr = '') {
                let self     = this;
                self.visible = true;
                if (!abbr) return;
                self.$nextTick(function () {
                    this.form = Object.assign({}, this.riskAreaData[abbr]);
                    this.old  = Object.assign({}, this.riskAreaData[abbr]);
                });
            },
            submitForm: async function () {
                let validated = await this.$refs.riskAreaForm.validate().then(() => true).catch(() => false);
                if (!validated) return;
                if (this.old.id)
                    await this.update({abbreviation: this.old.abbreviation, modifiedRiskArea: this.form});
                else
                    await this.add([this.form]);
                this.visible = false;
            },
            resetForm() {
                if (this.$refs.riskAreaForm)
                    this.$refs.riskAreaForm.resetFields();
            },
            add:        async function (data) {
                this.$emit('update:loading', true);
                let resp = await API.riskIndex.riskArea.post(data);
                this.addState(resp.body.data);
                this.$emit('update:loading', false);
            },
            update:     async function ({abbreviation, modifiedRiskArea}) {
                this.$emit('update:loading', true);
                await API.riskIndex.riskArea.put(modifiedRiskArea);
                this.updateState({abbreviation, modifiedRiskArea: Object.assign({}, modifiedRiskArea)});
                this.$emit('update:loading', false);
            },
            delete:     async function (abbr) {
                let confirm = await this.$confirm(`Are you sure you want to delete Risk Area ${abbr}?`, 'Warning', {
                    confirmButtonText: 'OK', cancelButtonText: 'Cancel', type: 'warning'
                }).then(() => true).catch(() => false);
                if (!confirm) return;
                this.$emit('update:loading', true);
                await API.riskIndex.riskArea.delete({id: this.riskAreaData[abbr].id});
                this.deleteState(abbr);
                this.$emit('update:loading', false);
            }
        },
        computed: {
            ...mapState([
                'riskAreaData'
            ])
        },
        watch:    {
            visible: function (shown) {
                if (!shown) {
                    this.$nextTick(function () {
                        this.resetForm();
                        this.old = {};
                        if (this.form.id) // delete id, which is added by update
                            delete this.form.id;
                    });
                }
            }
        }
    };
</script>

<style scoped>
    .dialog-footer {
        text-align: right;
    }
</style>